import { GlobalSearchResult } from "@/core";
import { serviceHost } from "@/services";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";

const functionsService = serviceHost.getFunctionsService();

// Terms shorter than this were never indexed as prefixes, so querying them would
// return nothing and only burn a round trip.
const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 250;

/**
 * Debounces a value so a keystroke does not become a request.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);

    return () => clearTimeout(timer);
  }, [value, delayMs]);

  return debounced;
}

/**
 * Runs the central search against the globalSearch callable.
 *
 * Previous results are kept while a new query is in flight so the list does not
 * blank out on every keystroke, and short queries resolve to nothing without a
 * request. `isSearching` covers the debounce window too, so the UI can show
 * progress from the first keypress rather than only once the request starts.
 */
export const useGlobalSearch = (payload: { query: string }) => {
  const trimmed = payload.query.trim();
  const debouncedQuery = useDebouncedValue(trimmed, DEBOUNCE_MS);
  const enabled = debouncedQuery.length >= MIN_QUERY_LENGTH;

  const query = useQuery<GlobalSearchResult>({
    queryKey: ["global-search", debouncedQuery],
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    queryFn: () => functionsService.globalSearch({ query: debouncedQuery }),
  });

  return {
    ...query,
    hits: enabled ? (query.data?.hits ?? []) : [],
    truncatedTypes: query.data?.truncatedTypes ?? [],
    isQueryTooShort: trimmed.length > 0 && trimmed.length < MIN_QUERY_LENGTH,
    isSearching: enabled && (query.isFetching || debouncedQuery !== trimmed),
  };
};
